// HomeScreen.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { CourseCard } from '../components/CourseCard';

const ICON_ROTATION = [
  "M4 18c3-6 5-9 8-9s5 3 8 9 M10 9V5 M14 9V5",
  "M14 5l5 5-9 9H5v-5z M12 7l5 5",
  "M4 6h16v12H4z M8 10h4 M8 14h8",
  "M6 8l-3 4 3 4 M18 8l3 4-3 4 M14 4l-4 16",
  "M9 3v5L4 18a2 2 0 002 3h12a2 2 0 002-3l-5-10V3",
  "M7 4c6 4 4 12 10 16 M17 4c-6 4 -4 12 -10 16",
];

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  // House style: M. D. YY
  const mo = d.getMonth() + 1;
  const dy = d.getDate();
  const yr = String(d.getFullYear()).slice(-2);
  return `${mo}. ${dy}. ${yr}`;
}

function mapCourse(c, idx) {
  return {
    id: c.id,
    title: c.name,
    tint: c.tint || "blue",
    lastPracticed: formatDate(c.end_date),
    // Real mastery from /api/plan (0.0–1.0). A fresh course with zero
    // practice will show an empty bar — the bar fills as signals accumulate.
    progress: Math.round((c.mastery || 0) * 100),
    icon: ICON_ROTATION[idx % ICON_ROTATION.length],
  };
}

function EditableName() {
  const [name, setName] = React.useState(() => localStorage.getItem("didactic.userName") || "");
  const ref = React.useRef(null);

  const commit = (value) => {
    const v = (value || "").replace(/\s+/g, " ").trim();
    setName(v);
    localStorage.setItem("didactic.userName", v);
  };

  return (
    <span
      ref={ref}
      className={"editable-name" + (name ? "" : " editable-name-empty")}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onBlur={(e) => commit(e.currentTarget.textContent)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
      }}
    >{name}</span>
  );
}

export function HomeScreen() {
  const navigate = useNavigate();
  const onOpenCourse = (c) => navigate('/plan', { state: { focusCourseId: c.id } });
  const onCreate = () => navigate('/study');
  const [courses, setCourses] = React.useState(null); // null = loading

  React.useEffect(() => {
    api.getPlan()
      .then((data) => {
        const raw = (data && data.courses) ? data.courses : [];
        setCourses(raw.map(mapCourse));
      })
      .catch(() => setCourses([]));
  }, []);

  const recents = courses ? courses.slice(0, 2) : [];
  const allCourses = courses || [];
  const hasAny = allCourses.length > 0;

  return (
    <section className="home">
      <header className="home-head">
        <div className="eyebrow">Welcome, <EditableName />.</div>
        <h1 className="serif-head">Let's get back to the action!</h1>
      </header>

      <button className="create-card" onClick={onCreate}>
        <div className="plus">
          <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="1.75" fill="none">
            <path d="M12 5v14M5 12h14"/>
          </svg>
        </div>
        <div className="create-label">Create a new course</div>
      </button>

      {courses === null && null}

      {courses !== null && !hasAny && (
        <p className="d-body-sm" style={{ marginTop: "1.25rem", opacity: 0.55 }}>
          No classes yet. Import your first syllabus to start.
        </p>
      )}

      {hasAny && (
        <>
          <div className="section-label">Recents</div>
          <div className="courses-row">
            {recents.map((c) => (
              <div key={c.id} onClick={() => onOpenCourse(c)} role="button">
                <CourseCard course={c} />
              </div>
            ))}
          </div>

          <div className="section-label">All courses</div>
          <div className="courses-grid">
            {allCourses.map((c) => (
              <div key={c.id} onClick={() => onOpenCourse(c)} role="button">
                <CourseCard course={c} />
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

